import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Download, Play, Users, Star, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ParticleBackground } from './particles';

export function Hero() {
  const [activeWord, setActiveWord] = useState(0);
  const words = ['Type', 'Write', 'Create', 'Compose', 'Draft'];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWord((prev) => (prev + 1) % words.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Particle Background */}
      <ParticleBackground />
      
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-accent/5 rounded-full blur-3xl animate-pulse delay-2000" />
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              {/* Social Proof Badges */}
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full">
                  <Star className="h-4 w-4 text-primary fill-primary" />
                  <span className="text-sm font-medium">4.9/5 Rating</span>
                </div>
                <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">50K+ Users</span>
                </div>
                <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full">
                  <Zap className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Real-time</span>
                </div>
              </div>

              {/* Main Headline */}
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                  Stop Typing.
                  <br />
                  <span className="text-primary relative">
                    Start Speaking
                    <svg
                      className="absolute -bottom-2 left-0 w-full"
                      viewBox="0 0 300 10"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M0,5 Q150,0 300,5"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="none"
                        className="animate-draw"
                      />
                    </svg>
                  </span>
                </h1>
                <p className="text-xl text-muted-foreground max-w-lg">
                  Transform your voice into text instantly with our revolutionary Windows app. 
                  Never let slow typing hold back your ideas again.
                </p>
              </div>

              {/* Animated Text */}
              <div className="h-8 flex items-center text-lg">
                <span className="text-muted-foreground">Perfect for when you need to </span>
                <span className="ml-2 font-semibold text-primary relative overflow-hidden">
                  <span
                    className="inline-block transition-all duration-500"
                    style={{
                      transform: `translateY(-${activeWord * 100}%)`,
                    }}
                  >
                    {words.map((word, index) => (
                      <span key={index} className="block h-8 leading-8">
                        {word}
                      </span>
                    ))}
                  </span>
                </span>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="group shadow-lg shadow-primary/25">
                  <Download className="mr-2 h-5 w-5 transition-transform group-hover:translate-y-0.5" />
                  Download Free
                  <span className="ml-2 text-sm opacity-75">Windows 10/11</span>
                </Button>
                <Button size="lg" variant="outline" className="group">
                  <Play className="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
                  Watch Demo
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span>30-day money back</span>
                </div>
              </div>
            </div>

            {/* Right Content - Live Demo */}
            <div className="relative">
              <Card className="p-6 backdrop-blur-sm bg-background/50 border-primary/20 shadow-2xl">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Live Demo</h3>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-sm text-muted-foreground">Ready</span>
                    </div>
                  </div>
                  
                  {/* Waveform Animation */}
                  <div className="h-24 flex items-center justify-center gap-1">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <div
                        key={i}
                        className="w-1 bg-primary/60 rounded-full animate-wave"
                        style={{
                          height: `${Math.random() * 60 + 20}px`,
                          animationDelay: `${i * 0.05}s`,
                        }}
                      />
                    ))}
                  </div>

                  {/* Demo Text Output */}
                  <div className="p-4 bg-muted/50 rounded-lg min-h-[100px]">
                    <p className="text-sm font-mono animate-typing">
                      "Welcome to the future of text input. Just speak naturally and watch your words appear instantly on screen..."
                    </p>
                  </div>

                  {/* Demo Controls */}
                  <div className="flex gap-2">
                    <Button className="flex-1" size="sm">
                      Try It Now
                    </Button>
                    <Button variant="outline" size="sm">
                      See More Examples
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-full animate-bounce">
                NEW!
              </div>
              <div className="absolute -bottom-6 -left-6 bg-card border rounded-lg p-3 shadow-lg animate-float">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-xs">🎯</span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold">99.9% Accuracy</p>
                    <p className="text-xs text-muted-foreground">Industry Leading</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}